import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { preRegistrationsApi } from "../../preRegistrations/api/preRegistrations";
import { SectionCard } from "./SectionCard";

const QUERY_KEY = ["pre-registration-notification-recipients", "global"];

function initials(name: string, email: string): string {
  if (name.trim()) {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

export const NotificationRecipientsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [addError, setAddError] = useState<string | null>(null);

  const recipientsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => preRegistrationsApi.listNotificationRecipients({ scope: "global" }),
    staleTime: 2 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      preRegistrationsApi.createNotificationRecipient({
        name: form.name.trim() || undefined,
        email: form.email.trim(),
        client_id: null,
      }),
    onSuccess: () => {
      setForm({ name: "", email: "" });
      setAddError(null);
      setAddOpen(false);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: any) => {
      setAddError(err?.response?.data?.message ?? "Failed to add recipient");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (recipientId: string) =>
      preRegistrationsApi.deleteNotificationRecipient(recipientId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const handleOpenAdd = () => {
    setForm({ name: "", email: "" });
    setAddError(null);
    setAddOpen(true);
  };

  const recipients = recipientsQuery.data?.data ?? [];

  return (
    <Stack spacing={2.5}>
      <SectionCard
        title="Global notification recipients"
        description="These addresses receive an email whenever any pre-registration is submitted across all clients."
        action={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add recipient
          </Button>
        }
      >
        {recipientsQuery.isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Stack>
        ) : recipientsQuery.isError ? (
          <Alert severity="error">Failed to load recipients.</Alert>
        ) : recipients.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <NotificationsNoneIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">No recipients configured.</Typography>
            <Typography variant="caption">Click "Add recipient" to add one.</Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {recipients.map((r) => (
              <Stack
                key={r.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 1.25 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: "primary.main",
                    flexShrink: 0,
                  }}
                >
                  {initials(r.name || "", r.email)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {r.name ? (
                    <>
                      <Typography variant="body2" fontWeight={500} noWrap>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{r.email}</Typography>
                    </>
                  ) : (
                    <Typography variant="body2" noWrap>{r.email}</Typography>
                  )}
                </Box>
                <Tooltip title="Remove recipient">
                  <IconButton
                    size="small"
                    color="error"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(r.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add notification recipient</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Name (optional)"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <TextField
              size="small"
              fullWidth
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && form.email.trim()) createMutation.mutate();
              }}
              required
            />
            {addError && <Alert severity="error">{addError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.email.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? <CircularProgress size={18} /> : "Add recipient"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
